import { AuthLoginResponse, AuthOption, AuthRole, ResponseValueObject } from '@gishub/domain';

/** Única credencial válida del endpoint simulado (fase sin backend real). */
const MOCK_CREDENTIALS = { username: 'superadmin', password: 'Super2026' } as const;

const MOCK_TOKEN_TTL_SECONDS = 8 * 60 * 60;
const MOCK_LATENCY_MS = 500;

/** Aliases de opciones de menú que `MenuNotifier` usa para filtrar ítems no públicos. */
const MOCK_OPTION_ALIASES = ['sisfor00507', 'sisfor00505', 'sisfor00802', 'sisfor01101'] as const;

export interface MockLoginParams {
  username: string;
  password: string;
  alias: string;
  recaptchaToken?: string;
}

/**
 * Endpoint de autenticación simulado: sin HTTP, valida credenciales locales
 * y emite un JWT de formato válido para que JwtUtil/SessionTimeout funcionen.
 */
export class MockAuthDatasource {
  async login(params: MockLoginParams): Promise<ResponseValueObject<AuthLoginResponse>> {
    await MockAuthDatasource.delay(MOCK_LATENCY_MS);

    const username = params.username.trim();
    if (username !== MOCK_CREDENTIALS.username || params.password !== MOCK_CREDENTIALS.password) {
      return new ResponseValueObject<AuthLoginResponse>(false, undefined, 'Usuario o contraseña incorrectos');
    }

    return new ResponseValueObject(true, MockAuthDatasource.buildResponse(username));
  }

  private static buildResponse(username: string): AuthLoginResponse {
    const token = MockAuthDatasource.buildFakeJwt(username);
    const roles: AuthRole[] = [
      {
        roleId: 1,
        options: MOCK_OPTION_ALIASES.map((alias, index) => MockAuthDatasource.buildOption(index + 1, alias)),
      },
    ];

    return {
      token,
      accessToken: token,
      userId: 1,
      username,
      fullName: 'Super Administrador MIDAGRI',
      email: 'superadmin@midagri.gob.pe',
      documentNumber: '00000000',
      roles,
    };
  }

  private static buildOption(optionId: number, alias: string): AuthOption {
    return {
      optionId,
      parentId: null,
      systemId: 1,
      description: null,
      alias,
      type: 1,
      area: null,
      controller: null,
      action: null,
      url: null,
      createdAt: null,
      updatedAt: null,
      createdBy: null,
      updatedBy: null,
      icon: null,
      position: null,
      version: null,
      dailyLimit: null,
    };
  }

  /** La firma es decorativa: JwtUtil solo decodifica el payload para leer `exp`. */
  private static buildFakeJwt(username: string): string {
    const encode = (value: object): string => {
      const base64 = btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(value))));
      return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    };

    const nowSeconds = Math.floor(Date.now() / 1000);
    return [
      encode({ alg: 'HS256', typ: 'JWT' }),
      encode({
        sub: username,
        unique_name: username,
        role: 'SUPERADMIN',
        simulated: true,
        iat: nowSeconds,
        exp: nowSeconds + MOCK_TOKEN_TTL_SECONDS,
      }),
      encode({ signature: 'mock' }),
    ].join('.');
  }

  private static delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
